import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const loadingVariants = cva(
  "animate-spin",
  {
    variants: {
      size: {
        xs: "h-3 w-3",
        sm: "h-4 w-4", 
        default: "h-5 w-5",
        md: "h-6 w-6",
        lg: "h-8 w-8",
        xl: "h-12 w-12",
      },
      variant: {
        default: "text-primary",
        muted: "text-muted-foreground",
        white: "text-white",
        green: "text-green-600",
      }
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  }
);

export interface LoadingProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingVariants> {
  /** Text to display next to spinner */
  text?: string;
  /** Center the loading indicator in its container */
  center?: boolean;
  /** Display as full page overlay */
  fullPage?: boolean;
  /** Overlay opacity for fullPage mode */
  overlay?: boolean;
}

const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  ({ className, size, variant, text, center, fullPage, overlay = false, ...props }, ref) => {
    // Full page loading state
    if (fullPage) {
      return (
        <div 
          className={cn(
            "fixed inset-0 z-50 flex flex-col items-center justify-center",
            overlay ? "bg-background/80 backdrop-blur-sm" : "bg-background"
          )}
          ref={ref}
          {...props}
        >
          <Loader2 className={cn(loadingVariants({ size: size || "xl", variant }))} />
          {text && (
            <p className={cn(
              "mt-4 text-sm font-medium",
              variant === "white" ? "text-white" : 
              variant === "muted" ? "text-muted-foreground" : 
              variant === "green" ? "text-green-600" : "text-foreground"
            )}>
              {text}
            </p>
          )}
        </div>
      );
    }

    const Container = center ? "div" : React.Fragment;
    const containerProps = center 
      ? { className: "flex items-center justify-center", ...props } 
      : props;

    return (
      <Container {...(center ? containerProps : {})}>
        <div 
          className={cn("flex items-center gap-2", center ? "" : className)} 
          ref={ref} 
          {...(center ? {} : props)}
        >
          <Loader2 className={cn(loadingVariants({ size, variant }))} />
          {text && (
            <span className={cn(
              "text-sm font-medium",
              variant === "white" ? "text-white" : 
              variant === "muted" ? "text-muted-foreground" : 
              variant === "green" ? "text-green-600" : "text-foreground"
            )}>
              {text}
            </span>
          )}
        </div>
      </Container>
    );
  }
);

Loading.displayName = "Loading";

/**
 * PageLoading - Full page loading indicator
 * Use when loading an entire page or major section
 */
export function PageLoading({ 
  text = "Loading...", 
  overlay = false 
}: { 
  text?: string; 
  overlay?: boolean;
}) {
  return <Loading fullPage text={text} overlay={overlay} size="xl" />;
}

/**
 * InlineLoading - Compact loading indicator for inline use
 * Use within buttons, form fields, or inline text
 */
export function InlineLoading({ 
  text,
  size = "sm",
  className,
}: { 
  text?: string;
  size?: "xs" | "sm" | "default" | "md" | "lg" | "xl";
  className?: string;
}) {
  return <Loading text={text} size={size} className={className} />;
}

/**
 * SectionLoading - Loading indicator for a section of the page
 * Provides consistent centering and sizing
 */
export function SectionLoading({ 
  text = "Loading...",
  minHeight = "200px",
}: { 
  text?: string;
  minHeight?: string;
}) {
  return (
    <div className="flex items-center justify-center" style={{ minHeight }}>
      <Loading text={text} size="lg" center />
    </div>
  );
}

export { Loading, loadingVariants };
